import React, {useEffect, useMemo, useState} from "react";
import {Button, Form} from "react-bootstrap";
import ScheduleGenerationProgress from "./ScheduleGenerationProgress";

const criterionLabels = {
  LOWEST_COST: "Najniższy rzeczywisty koszt roboczogodziny",
  HIGHEST_EVALUATION: "Najwyższa ogólna ocena pracownika",
  FEWEST_WORKING_DAYS: "Najmniej dni pracy w bieżącym tygodniu",
  PREFERRED_AVAILABILITY: "Preferowana dostępność pracownika"
};

const criterionDescriptions = {
  LOWEST_COST: "Pełny koszt pracodawcy liczony przez centralny kalkulator kosztów pracy dla pracownika, stanowiska i dnia zmiany.",
  HIGHEST_EVALUATION: "Bieżąca ogólna ocena pracownika wyliczana z ocen umiejętności miękkich, stanowisk, szkoleń oraz zdarzeń krytycznych zgodnie z wagami w ustawieniach.",
  FEWEST_WORKING_DAYS: "Preferuje osoby z mniejszą liczbą już zaplanowanych dni pracy w tygodniu obejmującym daną zmianę.",
  PREFERRED_AVAILABILITY: "Preferuje pracownika, który oznaczył cały zakres zmiany jako preferowany, a nie tylko dostępny."
};

const fallbackCriteria = [
  "LOWEST_COST",
  "HIGHEST_EVALUATION",
  "FEWEST_WORKING_DAYS",
  "PREFERRED_AVAILABILITY"
];

const periodTypeLabels = {
  DAILY: "dzienny",
  WEEKEND: "weekendowy",
  WEEKLY: "tygodniowy",
  BIWEEKLY: "dwutygodniowy",
  MONTHLY: "miesięczny",
  CUSTOM: "dowolny okres"
};

const parseDate = (value) => {
  const [y, m, d] = String(value).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1, 12, 0, 0, 0);
};

const dateToString = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const addDays = (value, days) => {
  const date = parseDate(value);
  date.setDate(date.getDate() + days);
  return dateToString(date);
};

const nextOrSameWeekday = (value, targetJsDay) => {
  const date = parseDate(value);
  const delta = (targetJsDay - date.getDay() + 7) % 7;
  date.setDate(date.getDate() + delta);
  return dateToString(date);
};

const firstDayOfNextMonth = (value) => {
  const date = parseDate(value);
  if (date.getDate() === 1) return dateToString(date);
  return dateToString(new Date(date.getFullYear(), date.getMonth() + 1, 1, 12));
};

const lastDayOfMonth = (value) => {
  const date = parseDate(value);
  return dateToString(new Date(date.getFullYear(), date.getMonth() + 1, 0, 12));
};

const suggestedStart = (schedule, plan) => {
  if (!plan) return schedule.dateFrom;
  switch (plan.periodType || "DAILY") {
    case "WEEKEND": return nextOrSameWeekday(schedule.dateFrom, 6);
    case "WEEKLY":
    case "BIWEEKLY": return nextOrSameWeekday(schedule.dateFrom, 1);
    case "MONTHLY": return firstDayOfNextMonth(schedule.dateFrom);
    default: return schedule.dateFrom;
  }
};

const periodEnd = (start, plan) => {
  if (!start || !plan) return start;
  switch (plan.periodType || "DAILY") {
    case "WEEKEND": return addDays(start, 1);
    case "WEEKLY": return addDays(start, 6);
    case "BIWEEKLY": return addDays(start, 13);
    case "MONTHLY": return lastDayOfMonth(start);
    case "CUSTOM": return addDays(start, Math.max(1, Number(plan.periodDays || 1)) - 1);
    default: return start;
  }
};

const formatTime = (value) => value ? value.slice(11, 16) : "";
const formatDate = (value) => value ? value.slice(0, 10) : "";
const formatNumber = (value, digits = 2) => value == null ? "—" : Number(value).toLocaleString("pl-PL", {minimumFractionDigits: digits, maximumFractionDigits: digits});

const ScheduleGenerationPanel = ({schedule, plans, result, progress, onGenerate, onCancelGeneration, saving, preferredPlanId, defaultGenerationCriteria}) => {
  const resolvedDefaultCriteria = useMemo(() => {
    const configured = Array.isArray(defaultGenerationCriteria)
      ? defaultGenerationCriteria.filter(item => fallbackCriteria.includes(item))
      : [];
    return [...new Set([...configured, ...fallbackCriteria])];
  }, [defaultGenerationCriteria]);
  const [form, setForm] = useState({
    periodStart: schedule.dateFrom,
    resourcePlanId: "",
    criteria: [...fallbackCriteria],
    replaceGenerated: false,
    applyWeatherDemandAdjustment: true
  });
  const [expandedLines, setExpandedLines] = useState({});

  const selectedPlan = useMemo(
    () => plans.find(plan => String(plan.id) === String(form.resourcePlanId)) || null,
    [plans, form.resourcePlanId]
  );

  useEffect(() => {
    const selected = plans.find(plan => String(plan.id) === String(preferredPlanId))
      || plans.find(plan => String(plan.id) === String(form.resourcePlanId))
      || plans.find(plan => plan.departmentDefault)
      || plans[0]
      || null;
    setForm(current => ({
      ...current,
      resourcePlanId: selected?.id ? String(selected.id) : "",
      periodStart: suggestedStart(schedule, selected),
      criteria: [...resolvedDefaultCriteria]
    }));
  }, [schedule.id, schedule.dateFrom, schedule.dateTo, plans, preferredPlanId, resolvedDefaultCriteria]);

  useEffect(() => {
    setExpandedLines({});
  }, [result]);

  const computedEnd = periodEnd(form.periodStart, selectedPlan);
  const periodFitsSchedule = !!form.periodStart && !!computedEnd
    && form.periodStart >= schedule.dateFrom
    && computedEnd <= schedule.dateTo;

  const handleChange = (event) => {
    const {name, value, type, checked} = event.target;
    if (name === "resourcePlanId") {
      const plan = plans.find(item => String(item.id) === String(value)) || null;
      setForm(current => ({...current, resourcePlanId: value, periodStart: suggestedStart(schedule, plan)}));
      return;
    }
    if (name === "month") {
      setForm(current => ({...current, periodStart: value ? `${value}-01` : ""}));
      return;
    }
    setForm(current => ({...current, [name]: type === "checkbox" ? checked : value}));
  };

  const moveCriterion = (index, direction) => {
    setForm(current => {
      const target = index + direction;
      if (target < 0 || target >= current.criteria.length) return current;
      const criteria = [...current.criteria];
      [criteria[index], criteria[target]] = [criteria[target], criteria[index]];
      return {...current, criteria};
    });
  };

  const resetCriteria = () => {
    setForm(current => ({...current, criteria: [...resolvedDefaultCriteria]}));
  };

  const submit = (event) => {
    event.preventDefault();
    if (!periodFitsSchedule) return;
    onGenerate({
      periodStart: form.periodStart,
      resourcePlanId: Number(form.resourcePlanId),
      criteria: form.criteria,
      replaceGenerated: form.replaceGenerated,
      applyWeatherDemandAdjustment: form.applyWeatherDemandAdjustment
    });
  };

  const toggleLineRanking = (key) => {
    setExpandedLines(current => ({...current, [key]: !current[key]}));
  };

  const startLabel = selectedPlan?.periodType === "MONTHLY"
    ? "Miesiąc"
    : selectedPlan?.periodType === "WEEKEND"
      ? "Początek (sobota)"
      : ["WEEKLY", "BIWEEKLY"].includes(selectedPlan?.periodType)
        ? "Początek (poniedziałek)"
        : "Początek okresu";

  return (
    <div className="schedule-section schedule-generation-section">
      <div className="schedule-section-heading">
        <div>
          <h6>Generowanie z planu zapotrzebowania</h6>
          <div className="schedule-section-subtitle">Jedno uruchomienie generuje cały okres zdefiniowany przez plan. Generator działa tylko w obrębie działu tego grafiku.</div>
        </div>
      </div>

      {plans.length === 0 ? (
        <div className="schedule-empty">Brak aktywnego planu zapotrzebowania zawierającego pozycje tego działu.</div>
      ) : (
        <form className="schedule-generation-form" onSubmit={submit}>
          <Form.Group>
            <Form.Label>Plan zapotrzebowania</Form.Label>
            <Form.Select name="resourcePlanId" value={form.resourcePlanId} onChange={handleChange} required>
              <option value="">Wybierz plan</option>
              {plans.map(plan => (
                <option key={plan.id} value={plan.id}>
                  {plan.name}{plan.departmentDefault ? " — domyślny dla działu" : ""} — {periodTypeLabels[plan.periodType] || "dzienny"} ({plan.configuredDays || 0} dni / {plan.itemCount} pozycji)
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Label>{startLabel}</Form.Label>
            {selectedPlan?.periodType === "MONTHLY" ? (
              <Form.Control type="month" name="month" value={form.periodStart ? form.periodStart.slice(0, 7) : ""} onChange={handleChange} required/>
            ) : (
              <Form.Control type="date" name="periodStart" min={schedule.dateFrom} max={schedule.dateTo} value={form.periodStart || ""} onChange={handleChange} required/>
            )}
            {selectedPlan && form.periodStart && (
              <Form.Text>Zakres generowania: {form.periodStart} – {computedEnd}</Form.Text>
            )}
            {selectedPlan && form.periodStart && !periodFitsSchedule && (
              <div className="text-danger small mt-1">Wybrany okres nie mieści się w zakresie grafiku {schedule.dateFrom} – {schedule.dateTo}.</div>
            )}
          </Form.Group>

          <Form.Group className="schedule-generation-priorities">
            <div className="d-flex justify-content-between align-items-center gap-2">
              <Form.Label className="mb-0">Priorytety przydziału</Form.Label>
              <Button type="button" variant="outline-secondary" size="sm" onClick={resetCriteria}>Przywróć domyślne</Button>
            </div>
            <div className="schedule-priority-list mt-2">
              {form.criteria.map((criterion, index) => (
                <div className="schedule-priority-row" key={criterion} title={criterionDescriptions[criterion] || ""}>
                  <span className="schedule-priority-number">{index + 1}</span>
                  <span className="schedule-priority-label">
                    {criterionLabels[criterion] || criterion}
                    <small className="d-block text-muted">{criterionDescriptions[criterion]}</small>
                  </span>
                  <div className="schedule-priority-actions">
                    <Button type="button" variant="outline-secondary" size="sm" disabled={index === 0} onClick={() => moveCriterion(index, -1)} aria-label="Przesuń kryterium wyżej">↑</Button>
                    <Button type="button" variant="outline-secondary" size="sm" disabled={index === form.criteria.length - 1} onClick={() => moveCriterion(index, 1)} aria-label="Przesuń kryterium niżej">↓</Button>
                  </div>
                </div>
              ))}
            </div>
            <Form.Text>
              Kryteria są stosowane dokładnie od góry. Przycisk przywraca domyślną kolejność ustawioną dla działu. Ogólna ocena jest liczona na bieżąco przed generowaniem, a nie odczytywana wyłącznie z ostatnio zapisanej wartości profilu. Przy remisie generator pilnuje minimalnych godzin tygodniowych, wyrównuje liczbę godzin, bierze pod uwagę ocenę na konkretnym stanowisku i dopiero na końcu nazwisko. Wynik generatora nie blokuje późniejszej ręcznej zmiany obsady.
            </Form.Text>
          </Form.Group>

          <Form.Group className="schedule-generation-replace">
            <Form.Check
              type="checkbox"
              name="applyWeatherDemandAdjustment"
              checked={form.applyWeatherDemandAdjustment}
              onChange={handleChange}
              label="Uwzględnij korektę zapotrzebowania wg pogody"
            />
            <Form.Text>Jeżeli dla dnia ustawiono korektę pogodową w panelu Sprzedaż, prognozy i budżet pracy, generator przeliczy bazową liczbę wymaganych osób przed przydziałem.</Form.Text>
          </Form.Group>

          <Form.Group className="schedule-generation-replace">
            <Form.Check
              type="checkbox"
              name="replaceGenerated"
              checked={form.replaceGenerated}
              onChange={handleChange}
              label="Usuń wszystkie istniejące zmiany w wybranym okresie przed generowaniem"
            />
            {form.replaceGenerated && (
              <Form.Text className="text-danger">
                Zostaną usunięte wszystkie zmiany w tym okresie: wygenerowane, ręczne i nieobsadzone, niezależnie od planu, z którego powstały.
              </Form.Text>
            )}
          </Form.Group>

          <div className="schedule-generation-submit">
            <Button type="submit" variant="primary" disabled={saving || !form.resourcePlanId || !periodFitsSchedule}>Generuj cały okres</Button>
          </div>

          {progress?.scheduleId === schedule.id && progress?.status && (
            <div className="schedule-generation-progress-slot">
              <ScheduleGenerationProgress progress={progress} onCancel={onCancelGeneration} />
            </div>
          )}
        </form>
      )}

      {result && result.scheduleId === schedule.id && (
        <div className={`schedule-generation-result ${result.openShifts > 0 ? "schedule-generation-result-warning" : ""}`}>
          <div className="schedule-generation-summary">
            <strong>{result.dateFrom || result.date} – {result.dateTo || result.date} — {result.resourcePlanName}</strong>
            <span>dni: {result.generatedDays || 1}</span>
            <span>już obsadzone zmiany: {result.existingAssignments}</span>
            <span>przydzieleni pracownicy: {result.generatedAssignments}</span>
            <span>braki obsady: {result.openShifts}</span>
          </div>

          {(result.criteria?.length ?? 0) > 0 && (
            <div className="schedule-generation-used-priorities">
              <strong>Użyte priorytety:</strong> {result.criteria.map((criterion, index) => `${index + 1}. ${criterionLabels[criterion] || criterion}`).join(" → ")}
            </div>
          )}

          {result.openShifts > 0 && (
            <div className={`schedule-generation-explanation ${result.generatedAssignments === 0 ? "schedule-generation-explanation-strong" : ""}`}>
              <strong>{result.generatedAssignments === 0 ? "Generator nie przydzielił żadnego pracownika." : "Nie całe zapotrzebowanie zostało obsadzone."}</strong>
              <span>„Braki obsady” oznaczają otwarte zmiany bez przypisanego pracownika. Nie są one grafikiem pracowników. Poniżej widać, dla których dni, godzin i stanowisk zabrakło obsady.</span>
            </div>
          )}

          <div className="table-responsive mt-2">
            <table className="table table-sm align-middle schedule-generation-table">
              <thead><tr><th>Dzień</th><th>Stanowisko</th><th>Godziny</th><th>Zapotrzebowanie</th><th>Już obsadzone</th><th>Przydzieleni pracownicy</th><th>Braki obsady</th><th>Kandydaci</th></tr></thead>
              <tbody>
                {(result.lines ?? []).map((line, index) => {
                  const lineKey = `${line.resourcePlanItemId}-${line.startsAt}-${index}`;
                  const ranking = line.candidateRanking ?? [];
                  const rejected = line.rejectedCandidates ?? [];
                  const expanded = !!expandedLines[lineKey];
                  return (
                    <React.Fragment key={lineKey}>
                      <tr className={line.openShifts > 0 ? "schedule-generation-shortage" : ""}>
                        <td>{formatDate(line.startsAt)}</td>
                        <td>{line.positionName}</td>
                        <td>{formatTime(line.startsAt)}–{formatTime(line.endsAt)}</td>
                        <td>
                          <div>{line.requiredPeopleCount}</div>
                          {line.weatherDemandAdjustmentPercent != null && Number(line.weatherDemandAdjustmentPercent) !== 0 && (
                            <small className="text-muted">bazowo {line.baseRequiredPeopleCount ?? line.requiredPeopleCount} · pogoda {Number(line.weatherDemandAdjustmentPercent) > 0 ? "+" : ""}{formatNumber(line.weatherDemandAdjustmentPercent, 0)}%</small>
                          )}
                        </td>
                        <td>{line.existingAssignments}</td>
                        <td>{line.generatedAssignments}</td>
                        <td>{line.openShifts}</td>
                        <td>
                          {(ranking.length > 0 || rejected.length > 0) ? (
                            <Button type="button" size="sm" variant="outline-secondary" onClick={() => toggleLineRanking(lineKey)}>
                              {expanded ? "Ukryj" : ranking.length > 0 ? "Pokaż ranking" : "Dlaczego brak?"} ({ranking.length > 0 ? (line.eligibleCandidateCount ?? ranking.length) : (line.rejectedCandidateCount ?? rejected.length)})
                            </Button>
                          ) : <span className="text-muted">brak pracowników przypisanych do tego stanowiska</span>}
                        </td>
                      </tr>
                      {expanded && (ranking.length > 0 || rejected.length > 0) && (
                        <tr className="schedule-generation-ranking-row">
                          <td colSpan="8">
                            {ranking.length > 0 && (
                              <>
                                <div className="small mb-2">
                                  <strong>Ranking kandydatów według powyższej kolejności kryteriów.</strong>
                                  {(line.eligibleCandidateCount ?? 0) > ranking.length && <span> Pokazano pierwszych {ranking.length} z {line.eligibleCandidateCount} kwalifikujących się osób.</span>}
                                </div>
                                <div className="table-responsive">
                                  <table className="table table-sm table-bordered mb-0 schedule-generation-ranking-table">
                                    <thead>
                                      <tr>
                                        <th>#</th><th>Pracownik</th><th>Stanowisko</th><th>Koszt / h</th><th>Ocena ogólna</th><th>Ocena stanowiska</th><th>Dni / tydz.</th><th>Godz. / tydz.</th><th>Preferowana</th><th>Wynik</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {ranking.map(candidate => (
                                        <tr key={`${lineKey}-${candidate.employeeId}`} className={candidate.selected ? "table-success" : ""}>
                                          <td>{candidate.rank}</td>
                                          <td>{candidate.employeeName}</td>
                                          <td>{candidate.positionName || "—"}</td>
                                          <td>{candidate.costComplete ? `${formatNumber(candidate.totalHourlyCost)} zł` : "niepełny"}</td>
                                          <td>{formatNumber(candidate.overallEvaluation)}</td>
                                          <td>{candidate.positionEvaluation ?? "—"}</td>
                                          <td>{candidate.workingDaysInWeek ?? 0}</td>
                                          <td>{formatNumber(candidate.scheduledHoursInWeek)}</td>
                                          <td>{candidate.preferredAvailability ? "TAK" : "NIE"}</td>
                                          <td><strong>{candidate.selected ? "PRZYDZIELONY" : "rezerwowy"}</strong></td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </>
                            )}
                            {rejected.length > 0 && (
                              <div className={ranking.length > 0 ? "mt-3" : ""}>
                                <div className="small mb-2"><strong>Pracownicy odrzuceni przez reguły grafiku:</strong></div>
                                <div className="table-responsive">
                                  <table className="table table-sm table-bordered mb-0">
                                    <thead><tr><th>Pracownik</th><th>Stanowisko</th><th>Powód</th></tr></thead>
                                    <tbody>
                                      {rejected.map((candidate, rejectedIndex) => (
                                        <tr key={`${lineKey}-rejected-${candidate.employeeId}-${candidate.positionId}-${rejectedIndex}`}>
                                          <td>{candidate.employeeName}</td>
                                          <td>{candidate.positionName || "—"}</td>
                                          <td>{(candidate.reasons ?? []).join("; ") || "Nie spełnia warunków przydziału"}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleGenerationPanel;



